"use strict";
// Copyright (C) 2017-2026 Smart code 203358507
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var workbox_window_1 = require("workbox-window");
var common_1 = require("stremio/common");
var usePWA_1 = __importDefault(require("stremio/common/usePWA"));
var UPDATE_CHECK_INTERVAL = 30 * 60 * 1000;
var APPLY_TIMEOUT = 15 * 1000;
var clearApplyTimeout = function (runtime) {
    if (runtime.timeout !== null) {
        clearTimeout(runtime.timeout);
        runtime.timeout = null;
    }
};
var useServiceWorkerUpdater = function () {
    var shell = (0, common_1.usePlatform)().shell;
    var _a = (0, usePWA_1.default)(), isIOSPWA = _a[0], isStandalonePWA = _a[1];
    var _b = (0, react_1.useState)({ status: 'idle' }), state = _b[0], setState = _b[1];
    var _c = (0, react_1.useState)(false), dismissed = _c[0], setDismissed = _c[1];
    var runtimeRef = (0, react_1.useRef)({
        workbox: null,
        applying: false,
        timeout: null,
        checkForUpdate: null,
    });
    var appLike = shell.active || Boolean(isIOSPWA) || Boolean(isStandalonePWA);
    var dismissUpdate = (0, react_1.useCallback)(function () {
        setDismissed(true);
    }, []);
    var applyUpdate = (0, react_1.useCallback)(function () {
        if (state.status === 'reload-ready') {
            window.location.reload();
            return;
        }
        var runtime = runtimeRef.current;
        if (runtime.applying ||
            runtime.workbox === null ||
            (state.status !== 'ready' && state.status !== 'failed')) {
            return;
        }
        runtime.applying = true;
        clearApplyTimeout(runtime);
        setState({ status: 'applying' });
        runtime.timeout = setTimeout(function () {
            runtime.timeout = null;
            runtime.applying = false;
            setState({ status: 'failed' });
        }, APPLY_TIMEOUT);
        runtime.workbox.messageSkipWaiting();
    }, [state.status]);
    (0, react_1.useEffect)(function () {
        var serviceWorkerDisabled = process.env.SERVICE_WORKER_DISABLED;
        if (process.env.NODE_ENV !== 'production' ||
            serviceWorkerDisabled === 'true' ||
            serviceWorkerDisabled === true ||
            !('serviceWorker' in navigator)) {
            return;
        }
        var runtime = runtimeRef.current;
        var workbox = new workbox_window_1.Workbox('service-worker.js');
        var registered = false;
        var lastUpdateCheck = Date.now();
        runtime.workbox = workbox;
        var onWaiting = function (event) {
            setDismissed(false);
            setState({
                status: 'ready',
                autoApply: appLike && event.wasWaitingBeforeRegister === true,
            });
        };
        var onControlling = function (event) {
            if (!event.isUpdate) {
                return;
            }
            clearApplyTimeout(runtime);
            if (runtime.applying) {
                window.location.reload();
            }
            else {
                setDismissed(false);
                setState({ status: 'reload-ready' });
            }
        };
        var checkForUpdate = function () {
            var now = Date.now();
            if (!registered ||
                navigator.serviceWorker.controller === null ||
                now - lastUpdateCheck < UPDATE_CHECK_INTERVAL) {
                return;
            }
            lastUpdateCheck = now;
            workbox.update().catch(function (error) {
                console.error('SW update check failed: ', error);
            });
        };
        runtime.checkForUpdate = checkForUpdate;
        workbox.addEventListener('waiting', onWaiting);
        workbox.addEventListener('controlling', onControlling);
        workbox.register()
            .then(function () {
            registered = true;
        })
            .catch(function (error) {
            console.error('SW registration failed: ', error);
        });
        return function () {
            registered = false;
            workbox.removeEventListener('waiting', onWaiting);
            workbox.removeEventListener('controlling', onControlling);
            clearApplyTimeout(runtime);
            runtime.applying = false;
            runtime.checkForUpdate = null;
            if (runtime.workbox === workbox) {
                runtime.workbox = null;
            }
        };
    }, [appLike]);
    (0, react_1.useEffect)(function () {
        var onForeground = function () {
            var _a, _b;
            setDismissed(false);
            (_b = (_a = runtimeRef.current).checkForUpdate) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
        var onVisibilityChange = function () {
            if (document.visibilityState === 'visible') {
                onForeground();
            }
        };
        var onShellVisibilityChange = function (_a) {
            var visible = _a.visible;
            if (visible) {
                onForeground();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        shell.on('win-visibility-changed', onShellVisibilityChange);
        return function () {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            shell.off('win-visibility-changed', onShellVisibilityChange);
        };
    }, [shell]);
    return {
        state: state,
        dismissed: dismissed,
        applyUpdate: applyUpdate,
        dismissUpdate: dismissUpdate,
    };
};
exports.default = useServiceWorkerUpdater;
